import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';

/**
 * The properties for the Remove Members dialog.
 */
interface RemoveMembersDialogProps {
  /**
   * Controls whether the dialog is open.
   */
  open: boolean;
  /**
   * Function to close the dialog.
   */
  onClose: () => void;
  /**
   * Function to confirm the removal of members.
   */
  onConfirm: () => void;
  /**
   * Array of expected members to display.
   */
  members: any[];
  /**
   * Indicates whether a loading state is active.
   */
  isLoading: boolean;
}

/**
 * Dialog component to confirm the removal of expected members.
 * Displays a list of emails of the members to be removed.
 */
const RemoveMembersDialog = (props: RemoveMembersDialogProps) => {
  const { open, onClose, onConfirm, members, isLoading } = props;

  // Extract and sort emails alphabetically
  const sortedEmails = [...members]
    .map((member) => member.email)
    .sort((a, b) => a.localeCompare(b));

  /**
   * Custom onClose handler to prevent closing when isLoading is true.
   */
  const handleClose = (_: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (isLoading && reason === 'backdropClick') {
      // Do nothing when isLoading is true and backdrop is clicked
      return;
    }
    // Otherwise, call the onClose prop
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={isLoading} // Prevent closing with escape key when loading
    >
      <DialogTitle>Confirm Removal of Members</DialogTitle>
      <DialogContent>
        <DialogContentText>
          The following expected members will be removed and will no longer have
          access to the training. If you wish to remove other members who have
          already started the training, please contact us for assistance.
        </DialogContentText>
        <Divider sx={{ marginY: 2 }} />
        {/* Display a list of member emails */}
        <List>
          {sortedEmails.map((email, index) => (
            <ListItem key={index}>
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <Avatar sx={{ width: 24, height: 24 }}>
                  <PersonRoundedIcon sx={{ fontSize: '.8em' }} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={email} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={onConfirm}
          color="error"
          variant="contained"
          loading={isLoading}
          loadingPosition="end"
          endIcon={<PersonRemoveRoundedIcon />}
        >
          Remove Members
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveMembersDialog;
